'use client';

import { c as _c } from "react/compiler-runtime";
import { getClassNames } from '@sprd/sprd-component-kit/v2';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import React from 'react';
import clsx from 'clsx';
import { AdvertisementCardItem, AmplienceLinkType } from '@/types/amplience';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import ProductAdvertisementCard, { ProductAdvertisementCardProps } from '@/components/ProductAdvertisementCard/ProductAdvertisementCard';
import { AppearanceConfigProvider } from '@/components/ProductCard/AppearanceConfigProvider';
import ProductCard, { ProductCardProps } from '@/components/ProductCard/ProductCard';
import ProgressBar from '@/components/ProgressBar/ProgressBar';
import Slider, { SliderProps } from '@/components/Slider/Slider';
import { AppearanceImage } from '@/utility/appearanceConfig';
import { getModuleBackgroundStyles } from '@/utility/getModuleBackgroundStyles';
import SliderFilters from '../Slider/SliderFilters';
import { SliderHeader } from '../Slider/SliderHeader';
import { resolveFilterFromHash } from './resolveFilterFromHash';

// Cards beyond this index render their appearance swatches client-side once they
// scroll near the viewport, keeping the SSR HTML small for off-screen cards.
const SSR_APPEARANCE_CARDS = 5;
type FilterOption = {
  label: string;
  value: string;
  anchorId?: string;
};
export interface FilteredItems {
  filterValue: string;
  items: (ProductCardProps | AdvertisementCardItem)[];
}
export type FilteredProductSliderProps = {
  className?: SliderProps['className'];
  heading?: string;
  showAllLink?: AmplienceLinkType | AmplienceLinkType[];
  variant?: 'white' | 'gray' | 'black';
  children?: ReactNode;
  noPadding?: SliderProps['noPadding'];
  filters?: FilterOption[];
  filteredItems?: FilteredItems[];
  defaultFilter?: string;
  /** Deduplicated appearance lookup shared by all cards (see AppearanceConfigProvider). */
  appearanceConfig?: Record<string, AppearanceImage>;
};
export default function FilteredProductSlider(props) {
  const $ = _c(58);
  const {
    heading,
    showAllLink,
    className,
    children,
    noPadding,
    filters,
    filteredItems,
    defaultFilter,
    appearanceConfig,
    variant
  } = props;
  const [progress, setProgress] = useState(0);
  const [activeFilter, setActiveFilter] = useState(defaultFilter || filters?.[0]?.value || "");
  let t0;
  let t1;
  if ($[0] !== filters) {
    t0 = () => {
      const applyHash = () => {
        const target = resolveFilterFromHash(window.location.hash, filters);
        if (target) {
          setActiveFilter(target);
          setProgress(0);
        }
      };
      applyHash();
      window.addEventListener("hashchange", applyHash);
      return () => window.removeEventListener("hashchange", applyHash);
    };
    t1 = [filters];
    $[0] = filters;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const classNames = getClassNames(className);
  const {
    bgClass,
    textColor,
    isBackgroundBlack
  } = getModuleBackgroundStyles(variant);
  const isDesktop = useIsDesktop();
  let t2;
  bb0: {
    if (!filters || !filteredItems) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[4] !== activeFilter || $[5] !== filteredItems) {
      let t4;
      if ($[7] !== activeFilter) {
        t4 = fi => fi.filterValue === activeFilter;
        $[7] = activeFilter;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t3 = filteredItems.find(t4);
      $[4] = activeFilter;
      $[5] = filteredItems;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    const filtered = t3;
    let t4;
    if ($[9] !== filtered?.items) {
      t4 = filtered?.items || [];
      $[9] = filtered?.items;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t2 = t4;
  }
  const displayItems = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = filterValue => {
      setActiveFilter(filterValue);
      setProgress(0);
    };
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleFilterChange = t3;
  let t4;
  if ($[12] !== activeFilter || $[13] !== isDesktop) {
    t4 = (item, index) => {
      if ("type" in item && item.type === "advertisementCard") {
        return <ProductAdvertisementCard key={`${activeFilter}-ad-${index}`} image={item.image} title={item.title} cta={item.cta} isDesktop={isDesktop} isLazyLoadingOff={item.isLazyLoadingOff} />;
      }
      if (!("type" in item)) {
        return <ProductCard key={`${activeFilter}-product-${index}`} imageBaseUrl={item.imageBaseUrl} images={item.images} imageSizes="(min-width: 768px) 33vw, (min-width: 1024px) 25vw, 50vw" link={item.link} price={item.price} priceFormat={item.priceFormat} title={item.title} subTitle={item.subTitle} brand={item.brand} labels={item.labels} appearanceIds={item.appearanceIds} appearances={item.appearances} favorite={item.favorite} background={item.background} productTypeId={item.productTypeId} assortment={item.assortment} dataTracking={item.dataTracking} deferAppearances={index >= SSR_APPEARANCE_CARDS} />;
      }
    };
    $[12] = activeFilter;
    $[13] = isDesktop;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const renderSliderItem = t4;
  const T0 = AppearanceConfigProvider;
  let t5;
  if ($[15] !== appearanceConfig) {
    t5 = appearanceConfig ?? {};
    $[15] = appearanceConfig;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== filters) {
    t6 = filters?.filter(_temp).map(_temp2);
    $[17] = filters;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = clsx("gap-2", classNames.root);
  const t8 = clsx(bgClass, textColor);
  const t9 = isBackgroundBlack ? "text-white" : undefined;
  let t10;
  if ($[19] !== heading || $[20] !== showAllLink || $[21] !== t9) {
    t10 = <SliderHeader heading={heading} showAllLink={showAllLink} className={t9} />;
    $[19] = heading;
    $[20] = showAllLink;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== activeFilter || $[24] !== filters || $[25] !== handleFilterChange || $[26] !== isBackgroundBlack) {
    t11 = filters && filters.length > 0 && <SliderFilters filters={filters} activeFilter={activeFilter} onFilterChange={handleFilterChange} isBackgroundBlack={isBackgroundBlack} />;
    $[23] = activeFilter;
    $[24] = filters;
    $[25] = handleFilterChange;
    $[26] = isBackgroundBlack;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== classNames) {
    t12 = {
      ...classNames,
      root: "[--slides-to-show:2.15] sm:[--slides-to-show:3.15] md:[--slides-to-show:4.18] lg:[--slides-to-show:4.2] xl:[--slides-to-show:4.15]",
      navPrev: "!hidden bg-black text-white md:!flex lg:size-16",
      navNext: "!hidden bg-black text-white md:!flex lg:size-16"
    };
    $[28] = classNames;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== displayItems || $[31] !== renderSliderItem) {
    let t14;
    if ($[33] !== renderSliderItem) {
      t14 = (item_0, index_0) => renderSliderItem(item_0, index_0);
      $[33] = renderSliderItem;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    t13 = displayItems.map(t14);
    $[30] = displayItems;
    $[31] = renderSliderItem;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[35] !== activeFilter || $[36] !== noPadding || $[37] !== t12 || $[38] !== t13) {
    t14 = <Slider className={t12} showNavigation={true} onProgressChange={setProgress} noPadding={noPadding} key={activeFilter}>{t13}</Slider>;
    $[35] = activeFilter;
    $[36] = noPadding;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = displayItems.length > 4;
  let t16;
  if ($[40] !== progress || $[41] !== t15) {
    t16 = <div className="px-6 md:px-8 lg:px-16 xl:px-22.5"><ProgressBar progress={progress} isVisible={t15} /></div>;
    $[40] = progress;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t14 || $[46] !== t16 || $[47] !== t8) {
    t17 = <div className={t8}>{t10}{t11}{t14}{t16}</div>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t14;
    $[46] = t16;
    $[47] = t8;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== children || $[50] !== t17 || $[51] !== t7) {
    t18 = <section className={t7}>{t17}{children}</section>;
    $[49] = children;
    $[50] = t17;
    $[51] = t7;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== T0 || $[54] !== t18 || $[55] !== t5 || $[56] !== t6) {
    t19 = <T0 appearances={t5}>{t6}{t18}</T0>;
    $[53] = T0;
    $[54] = t18;
    $[55] = t5;
    $[56] = t6;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  return t19;
}
function _temp2(filter_0) {
  return <div key={filter_0.anchorId} id={filter_0.anchorId} className="scroll-mt-22.5" aria-hidden={true} />;
}
function _temp(filter) {
  return filter.anchorId;
}